import React, { useCallback, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConnectorsDialog from "@/components/Connectors/ConnectorsDialog";
import { GithubImportModal } from "@/components/Github/GithubImportModal";

// The route survives so /connectors stays shareable and bookmarkable, but the experience is the
// modal: connectors are something you reach for while describing what you want built, and a separate
// page loses that thread. Closing it returns you to where you were rather than leaving a dead screen.
//
// Every connector here opens the flow that actually configures it. The project-scoped ones (payments,
// sign-in) cannot be set up without a project, so from here they hand you to the home page with the
// request already written — a shorter path than explaining why a settings screen is unavailable.

const Connectors: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const projectId = params.get("project_id") || undefined;

  const [open, setOpen] = useState(true);
  const [githubOpen, setGithubOpen] = useState(false);
  // Set when a connector hands off somewhere else; the dialog closes right after, and that close must not also navigate back
  const handedOff = useRef(false);

  const leave = useCallback(() => {
    setOpen(false);
    // Back where they came from when there is history, home otherwise — a modal that closes onto a
    // blank route is the dead end this replaced
    if (window.history.length > 1) navigate(-1);
    else navigate("/~");
  }, [navigate]);

  return (
    <div>
      <ConnectorsDialog
        open={open}
        onOpenChange={(next) => {
          if (next) return;
          if (handedOff.current) setOpen(false);
          else leave();
        }}
        projectId={projectId}
        onOpenConnectorModal={(connector) => {
          handedOff.current = true;
          if (connector === "universal_ai_key") navigate("/universal-ai-key", { state: { returnTo: { pathname: "/connectors" } } });
          else if (connector === "github") setGithubOpen(true);
          else handedOff.current = false;
        }}
        onOpenProject={(url) => {
          // Without the hand-off flag, closing the dialog would run leave() and its navigate(-1) would undo this
          handedOff.current = true;
          navigate(url);
        }}
        onPrefillPrompt={(prompt) => {
          handedOff.current = true;
          // Handed to the prompt box on the home page, which reads this on mount
          sessionStorage.setItem("joylo_initial_prompt", prompt);
          navigate("/~");
        }}
      />

      <GithubImportModal
        isOpen={githubOpen}
        onClose={() => {
          setGithubOpen(false);
          leave();
        }}
      />
    </div>
  );
};

export default Connectors;
